/**
 * voice-v8 — Werkzeugkarten: E-Mail: Senden, Gmail-Suche, gelesene Mail, Entwürfe.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import type { ToolCardMessage } from "../webrtc.js";
import { absatz, aktionsKnopf, aktionsLeiste, baueKarte, fehlerBlock, kartenTitel, kuerzeListe, leerHinweis, liste, listenEintrag, weitereHinweis, wertZeile } from "../karte.js";
import { gekappterAnfang } from "../../../memory/metadaten-kappen.js";
import { mailDatum, mailEmpfaengerKurz, mailPersonKurz, mailPersonen, mailVorschau, ohneSchreibsteuer, zeigeMailText } from "../mail-text.js";
import { menschlicherFehler } from "../fehler-worte.js";
import { fmtDateShort, freigabeStand, ohneEigeneKarte, toolLabel, trimText, unwrapResult } from "./hilfe.js";
import { lesemodusKnopf } from "../lesemodus.js";
import { holeMail } from "../mail-lesen-anfrage.js";
import { mailIdAus, type MailLesenFehlerCode } from "../../../shared/mail-lesen.js";

interface MailResult {
  to?: string;
  subject?: string;
  messageId?: string;
  dryRun?: boolean;
  sent?: boolean;
  speak?: string;
  /** 25.10.2026: send_mail ist zweistufig (tools/send_mail.ts) — der erste Aufruf ist nur ein Vorschlag. */
  wartet_auf_bestaetigung?: boolean;
  text?: string;
  anhaenge?: number;
  /** Namen der Anhänge (Caption oder Dateiname). */
  anhang_namen?: unknown;
}

export function buildMailCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as MailResult;
  const el = baueKarte("mail");
  // Nur „verschickt“, wenn wirklich gesendet wurde — ein Vorschlag oder eine Ablehnung sagt das deutlich.
  const vorschlag = r.wartet_auf_bestaetigung === true;
  const nichtGesendet = !vorschlag && r.sent === false;
  el.appendChild(
    kartenTitel(
      vorschlag ? "Mail-Vorschlag — noch nicht gesendet" : nichtGesendet ? "Mail nicht gesendet" : r.dryRun ? "Mail (dry-run)" : "Mail verschickt",
    ),
  );
  // 25.10.2026 (Auflage 2 des Sicherheitsprüfers): im Vorschlag die VOLLE Adresse — ein Kurzname („Mama“) verbirgt, wohin
  // die Mail wirklich geht. Der Server lässt nur schlichte Adressen zu (tools/send_mail.ts `istSchlichteAdresse`).
  const an = typeof r.to === "string" ? r.to : "";
  wertZeile(el, "An", (vorschlag ? an : mailEmpfaengerKurz(an)) || "?");
  wertZeile(el, "Betreff", ohneSchreibsteuer(r.subject ?? "") || "?");
  if (vorschlag) {
    if (typeof r.text === "string" && r.text.trim()) el.appendChild(absatz(ohneSchreibsteuer(r.text)));
    // Auflage 4: die Anhänge mit Namen, nicht nur ihre Zahl.
    const namen = Array.isArray(r.anhang_namen)
      ? r.anhang_namen.filter((n): n is string => typeof n === "string" && n.trim() !== "").map((n) => trimText(ohneSchreibsteuer(n), 120))
      : [];
    if (namen.length > 0) wertZeile(el, "Anhänge", namen.join(", "));
    else if (typeof r.anhaenge === "number" && r.anhaenge > 0) wertZeile(el, "Anhänge", String(r.anhaenge));
    el.appendChild(absatz("Gesendet wird erst nach deinem Ja (60 Minuten gültig).", "hint"));
    freigabeStand(el);
  } else if (nichtGesendet && typeof r.speak === "string" && r.speak.trim()) {
    el.appendChild(absatz(trimText(r.speak, 200), "hint"));
  }
  return el;
}

// --- Gmail ---------------------------------------------------------------

interface GmailItem {
  id?: string;
  from?: string;
  subject?: string;
  snippet?: string;
  date?: string;
  webLink?: string;
  /** Optional: sobald die Suche es mitschickt (Gmail-Label UNREAD), zeigt der Eintrag den Ungelesen-Punkt. */
  unread?: boolean;
  labelIds?: string[];
}

export function buildGmailSearchCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("gmail-search");
  const items = (r.items as GmailItem[] | undefined) ?? [];
  const count = (r.count as number | undefined) ?? items.length;
  el.appendChild(kartenTitel(`Gmail · ${count} Treffer`));
  // 20.10.2026 (Lesemodus): ein Knopf an der Karte (lib/lesemodus.ts). Dass hier Text steht, merkt app.ts nur bei LIVE-Karten.
  el.appendChild(lesemodusKnopf());
  if (items.length === 0) {
    leerHinweis(el, "Keine Mails gefunden.");
    return el;
  }
  const ul = liste(el);
  const gekuerzt = kuerzeListe(items, 6);
  let mitKnopf = false;
  for (const it of gekuerzt.sichtbar) {
    const meta = it.date ? fmtDateShort(it.date) : undefined;
    // Ticket 2747: Absender als Name, Vorschau ohne Entities und Outlook-Reste.
    // 15.10.2026: sechs Zeilen, Betreff und Anriss gekürzt — ein Betreff ohne
    // Ende und ein Anriss von 50.000 Zeichen füllten die Karte (Karten-Größe).
    // 20.10.2026 (Martin/Albert: „lesen statt vorlesen“): Absender und Anriss
    // stehen je auf eigener Zeile — der Anriss ist der Anfang des Textes, nicht
    // 90 Zeichen nach dem Namen abgeschnitten (CSS kürzt ihn auf drei Zeilen).
    const zeilen = [trimText(mailPersonKurz(it.from ?? ""), 60), trimText(mailVorschau(it.snippet ?? ""), ANRISS_MAX)].filter((z) => z);
    const betreff = trimText(ohneSchreibsteuer(it.subject ?? "") || "(kein Betreff)", 80);
    const li = listenEintrag(betreff, zeilen, meta, it.webLink);
    if (it.unread === true || it.labelIds?.includes("UNREAD")) ungelesenPunkt(li);
    // 20.10.2026 (Punkt 2): „Lesen“ holt die ganze Mail ohne Vespera — nur mit gültiger Kennung (shared/mail-lesen.ts).
    const id = mailIdAus(it.id);
    if (id) {
      lesenKnopf(li, id, betreff);
      li.dataset.mailId = id;
      mitKnopf = true;
    }
    ul.appendChild(li);
  }
  // Die Kennungen aller Treffer: die Recherche-Gruppe (lib/recherche.ts) entdoppelt und erkennt „dieselbe Mail“ daran.
  el.dataset.mailIds = items.map((it) => mailIdAus(it.id)).filter((i): i is string => i !== null).join(" ");
  weitereHinweis(el, gekuerzt.rest);
  // Die Treffer tragen nur den Anriss — den ganzen Text holt das Lesen der Mail. Wer nicht hört, soll wissen, wie.
  const hinweis = absatz(mitKnopf ? GANZE_MAIL_HINWEIS : GANZE_MAIL_HINWEIS_SPRECHEN, "hint");
  hinweis.classList.add("tool-mail-ganz-hinweis");
  el.appendChild(hinweis);
  return el;
}

/** So lang ist der Anriss eines Treffers höchstens — sechs Treffer bleiben unter der Karten-Größe (karten-groesse.test.ts). */
const ANRISS_MAX = 140;

/** Unter der Trefferliste: wie der ganze Text auf die Karte kommt. */
export const GANZE_MAIL_HINWEIS = "Ganzen Text lesen: „Lesen“ antippen oder „Zeig mir die Mail von …“ schreiben oder sagen.";
/** Dasselbe, wenn kein Treffer einen Knopf trägt (Treffer ohne Kennung, etwa aus altem Verlauf). */
export const GANZE_MAIL_HINWEIS_SPRECHEN = "Ganzen Text lesen: „Zeig mir die Mail von …“ schreiben oder sagen.";

/** Steht da, wenn das Holen scheitert — dazu, wenn bekannt, warum. */
export const MAIL_LADEN_FEHLER = "Mail konnte nicht geladen werden.";

const FEHLER_GRUND: Partial<Record<MailLesenFehlerCode, string>> = {
  "nicht-verbunden": "Google ist nicht verbunden — in den Einstellungen unter Verbindungen verknüpfen.",
  "nicht-gefunden": "Die Mail ist im Postfach nicht mehr zu finden.",
  limit: "Zu viele Abrufe in der letzten Stunde — bitte später noch einmal.",
  aus: "Das direkte Lesen ist gerade abgeschaltet.",
  "not-logged-in": "Bitte neu anmelden.",
  "auth-revoked": "Bitte neu anmelden.",
};

let lesenZaehler = 0;

/** Der Punkt vor dem Betreff eines ungelesenen Treffers (CSS zeichnet ihn; der Name steht für den Vorleser da). */
function ungelesenPunkt(li: HTMLLIElement): void {
  const punkt = document.createElement("span");
  punkt.className = "tool-mail-ungelesen";
  punkt.setAttribute("role", "img");
  punkt.setAttribute("aria-label", "Ungelesen");
  li.querySelector(".tool-list-row")?.prepend(punkt);
}

/**
 * Der Knopf „Lesen“ unter einem Treffer (20.10.2026, Martin per Knopf-Ja, Punkt 2 — Albert liest statt zu hören).
 *
 * Ein Tipp holt die ganze Mail über die Lese-Route (lib/mail-lesen-anfrage.ts) und zeigt sie als Lesekarte UNTER dem Treffer —
 * nur hier im Browser: kein Modell, kein Verlauf, kein Mitlesestrom. Ein zweiter Tipp klappt sie zu und wieder auf, ohne neu zu holen.
 * Während des Holens zählt kein weiterer Tipp (Doppelklick-Schutz). Scheitert es: Satz + „Wiederholen“.
 */
function lesenKnopf(li: HTMLLIElement, id: string, betreff: string): void {
  const bereich = document.createElement("div");
  bereich.className = "tool-mail-lesen";
  bereich.id = `mail-lesen-${++lesenZaehler}`;
  bereich.hidden = true;
  const kurz = trimText(betreff, 40);
  const knopf = aktionsKnopf({ text: "Lesen", name: `Mail lesen: ${kurz}` });
  knopf.setAttribute("aria-expanded", "false");
  knopf.setAttribute("aria-controls", bereich.id);
  let laeuft = false;
  let geladen = false;

  const setzeOffen = (offen: boolean): void => {
    bereich.hidden = !offen;
    knopf.setAttribute("aria-expanded", String(offen));
    li.classList.toggle("tool-mail-aufgeklappt", offen);
    const wort = knopf.lastChild;
    if (wort) wort.textContent = offen ? "Schließen" : "Lesen";
    knopf.setAttribute("aria-label", `${offen ? "Mail schließen" : "Mail lesen"}: ${kurz}`);
  };

  const lade = async (): Promise<void> => {
    if (laeuft) return;
    laeuft = true;
    // Kam der Tipp vom „Wiederholen“ im Bereich, der gleich ersetzt wird: der Fokus bleibt beim Knopf des Treffers.
    if (bereich.contains(document.activeElement)) knopf.focus();
    knopf.setAttribute("aria-disabled", "true");
    bereich.setAttribute("aria-busy", "true");
    const warte = document.createElement("p");
    warte.className = "tool-mail-lesen-stand";
    warte.setAttribute("role", "status");
    warte.textContent = "Mail wird geladen …";
    bereich.replaceChildren(warte);
    setzeOffen(true);
    const r = await holeMail(id);
    laeuft = false;
    knopf.removeAttribute("aria-disabled");
    bereich.removeAttribute("aria-busy");
    if (r.ok) {
      geladen = true;
      const karte = buildGmailReadCard({ tool: "gmail_read_message", callId: `lesen-${id}`, isError: false, result: r.mail }, true);
      karte.classList.add("tool-mail-geladen");
      bereich.replaceChildren(karte);
      return;
    }
    const fehler = document.createElement("div");
    fehler.className = "tool-mail-lesen-fehler";
    fehler.setAttribute("role", "alert");
    const satz = document.createElement("p");
    satz.textContent = MAIL_LADEN_FEHLER;
    fehler.appendChild(satz);
    const grund = FEHLER_GRUND[r.error];
    if (grund) {
      const g = document.createElement("p");
      g.className = "tool-mail-lesen-grund";
      g.textContent = grund;
      fehler.appendChild(g);
    }
    bereich.replaceChildren(fehler);
    // „aus“ und „limit“: Wiederholen wäre sinnlos (Notschalter bzw. Stundengrenze) — kein Knopf.
    if (r.error !== "aus" && r.error !== "limit") {
      const nochmal = aktionsKnopf({ text: "Wiederholen", name: `Mail erneut laden: ${kurz}`, onClick: () => void lade() });
      aktionsLeiste(bereich).appendChild(nochmal);
      nochmal.focus();
    }
  };

  knopf.addEventListener("click", () => {
    if (laeuft) return;
    if (geladen) setzeOffen(knopf.getAttribute("aria-expanded") !== "true");
    else void lade();
  });
  aktionsLeiste(li).appendChild(knopf);
  li.appendChild(bereich);
  // Ein Tipp auf die Zeile ist derselbe Tipp wie auf „Lesen“ (kleine Karte, ganze Fläche tippbar). Knöpfe, Links, Auswahl und die geholte Mail bleiben unberührt.
  li.classList.add("tool-mail-tippbar");
  li.addEventListener("click", (ev) => {
    const ziel = ev.target as Element | null;
    if (!ziel || ziel.closest("a, button, .tool-mail-lesen") || window.getSelection()?.toString()) return;
    knopf.click();
  });
}

/*
 * Die gelesene Mail — 28.09.2026 (Ticket 2747).
 *
 * Martin: „Die Darstellung der E-Mail sieht katastrophal aus.“ Bis hierher
 * fiel gmail_read_message auf die allgemeine Karte: Feldliste, rohes
 * RFC-Datum, der ganze Text als EIN Block ohne Absätze. Jetzt: Betreff als
 * Überschrift, Absender mit Adresse klein, Datum deutsch in Wiener Zeit,
 * An/Cc knapp, Anhänge — und der Text gegliedert (frontend/lib/mail-text.ts,
 * die EINE Stelle der Bereinigung).
 */
export function buildGmailReadCard(msg: ToolCardMessage, ohneSchalter = false): HTMLElement {
  const roh = unwrapResult(msg.result);
  // Eine lange Mail liegt im Verlauf nur mit ihrem Anfang (memory/metadaten-kappen.ts).
  const anfang = gekappterAnfang(roh);
  const r = anfang ?? roh;
  if (r.ok === false || (typeof r.error === "string" && r.error)) {
    const el = baueKarte("gmail-read");
    el.appendChild(kartenTitel(toolLabel(msg.tool)));
    el.appendChild(fehlerBlock(menschlicherFehler(r, "Die Mail ließ sich nicht lesen.")));
    return el;
  }
  // Reicht der Anfang nicht einmal bis zum Text, gibt es keine Mail zu zeigen.
  if (typeof r.body !== "string") return ohneEigeneKarte(msg);
  const el = baueKarte("gmail-read");
  const betreff = (typeof r.subject === "string" ? ohneSchreibsteuer(r.subject).trim() : "") || "(kein Betreff)";
  el.setAttribute("aria-label", `E-Mail: ${betreff}`);
  const mailId = mailIdAus(r.id);
  if (mailId) el.dataset.mailId = mailId;
  const titel = document.createElement("h3");
  // 20.10.2026 (Lesen als Hauptweg): der Betreff ist die Überschrift des Briefs — größer als der Absender darunter.
  titel.className = "tool-card-title tool-mail-betreff";
  titel.textContent = betreff;
  el.appendChild(titel);
  // Pro Trefferkarte nur EIN Schalter: die über „Lesen“ geholte Mail (tool-mail-geladen) trägt keinen eigenen.
  if (!ohneSchalter) el.appendChild(lesemodusKnopf());
  el.appendChild(mailKopf(r));
  const anhaenge = (Array.isArray(r.anhaenge) ? r.anhaenge : [])
    .map((a) => (a as { dateiname?: unknown }).dateiname)
    .filter((n): n is string => typeof n === "string" && n.trim() !== "");
  if (anhaenge.length > 0) {
    const zeile = document.createElement("div");
    zeile.className = "tool-mail-anhaenge";
    zeile.appendChild(mailEtikett(anhaenge.length === 1 ? "Anhang" : "Anhänge"));
    const ul = document.createElement("ul");
    ul.setAttribute("aria-label", "Anhänge");
    for (const name of anhaenge) {
      const li = document.createElement("li");
      li.className = "tool-mail-anhang";
      li.textContent = name;
      ul.appendChild(li);
    }
    zeile.appendChild(ul);
    el.appendChild(zeile);
  }
  // Die Kennung der Mail merkt sich, ob Martin sie ganz aufgeklappt hat — über das Neuladen hinaus (mail-text.ts).
  if (r.body.trim()) zeigeMailText(el, r.body, typeof r.id === "string" ? r.id : undefined);
  else leerHinweis(el, "Die Mail hat keinen Text.");
  if (anfang) {
    // Ehrlich sagen, dass hier nicht die ganze Mail steht — der Rest (und die
    // Anhangliste dahinter) liegt nicht mehr im Verlauf.
    const rest = document.createElement("p");
    // mass-aus: eine Angabe unter einer Haarlinie, kein Fließtext mit Lesemaß.
    rest.className = "tool-mail-rest mass-aus";
    rest.textContent = "Die Mail war länger — im Verlauf ist nur ihr Anfang gespeichert.";
    el.appendChild(rest);
  }
  return el;
}

function mailEtikett(text: string): HTMLSpanElement {
  const s = document.createElement("span");
  s.className = "tool-mail-etikett";
  s.textContent = text;
  return s;
}

/** Absender (Name, Adresse klein), Datum, An/Cc. */
function mailKopf(r: Record<string, unknown>): HTMLElement {
  const kopf = document.createElement("div");
  kopf.className = "tool-mail-kopf";
  const von = mailPersonen(typeof r.from === "string" ? r.from : "")[0];
  if (von) {
    const zeile = document.createElement("p");
    zeile.className = "tool-mail-von";
    const name = document.createElement("span");
    name.className = "tool-mail-name";
    name.textContent = von.name || von.adresse;
    zeile.appendChild(name);
    if (von.name && von.adresse) {
      const adresse = document.createElement("span");
      adresse.className = "tool-mail-adresse";
      adresse.textContent = von.adresse;
      zeile.appendChild(adresse);
    }
    kopf.appendChild(zeile);
  }
  const datum = mailDatum(typeof r.date === "string" ? r.date : "");
  if (datum) {
    const t = document.createElement("time");
    t.className = "tool-mail-datum";
    if (datum.iso) t.dateTime = datum.iso;
    t.textContent = datum.text;
    kopf.appendChild(t);
  }
  const empfaenger: Array<[string, unknown]> = [
    ["An", r.to],
    ["Cc", r.cc],
  ];
  const an = document.createElement("p");
  an.className = "tool-mail-an";
  for (const [etikett, wert] of empfaenger) {
    const namen = typeof wert === "string" ? mailEmpfaengerKurz(wert) : "";
    if (!namen) continue;
    // Echte Leerzeichen und Trenner — ein Vorleseprogramm liest sonst „AnTest“.
    if (an.childNodes.length > 0) an.appendChild(document.createTextNode(" · "));
    an.append(mailEtikett(etikett), document.createTextNode(` ${namen}`));
  }
  if (an.childNodes.length > 0) kopf.appendChild(an);
  return kopf;
}

export function buildGmailSendCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("gmail-send");
  el.appendChild(kartenTitel("Mail gesendet"));
  const an = mailEmpfaengerKurz(String(r.to ?? ""));
  if (an) wertZeile(el, "An", an);
  if (r.subject) wertZeile(el, "Betreff", ohneSchreibsteuer(String(r.subject)));
  return el;
}

// Pre.344: Gmail-Draft-Cards — visuell deutlich anders als gesendete
// Mail. User sieht sofort "Entwurf" vs "gesendet".
export function buildGmailDraftCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("gmail-draft");
  // Kurz, ohne Kennung: „Entwurf an Name“, darunter der Betreff.
  const an = mailEmpfaengerKurz(String(r.to ?? ""));
  el.appendChild(kartenTitel(an ? `Entwurf an ${an}` : "Entwurf gespeichert"));
  if (r.subject) wertZeile(el, "Betreff", ohneSchreibsteuer(String(r.subject)));
  el.appendChild(absatz('Nicht versendet. Liegt im Gmail-Drafts-Ordner. Senden mit "Schick den Entwurf ab" oder direkt in Gmail prüfen.', "hint"));
  return el;
}

export function buildGmailDraftSendCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("gmail-draft-send");
  el.appendChild(kartenTitel("Entwurf versendet"));
  return el;
}

export function buildGmailDraftsListCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("gmail-drafts-list");
  const count = typeof r.count === "number" ? r.count : 0;
  // Kein zweiter Parameter: `kartenTitel(text, art)` setzt die Art als WORT davor — „mail“ stand so als roher Text
  // vor dem Titel (20.10.2026, Martin/Vespera). Ein Symbol kennt der Titel nicht; die Karte trägt ihre Art im Namen.
  el.appendChild(kartenTitel(`Entwürfe (${count})`));
  const items = Array.isArray(r.items) ? r.items : [];
  if (items.length === 0) {
    leerHinweis(el, "Keine Entwürfe im Postfach.");
  } else {
    // Dasselbe Raster wie die Gmail-Suche: Betreff als Hauptzeile, Empfänger darunter (Ticket 2747: mit Namen statt
    // „Name <adresse>“). Ohne Empfänger steht es klar da, nicht als „→ —“.
    const ul = liste(el);
    const gekuerzt = kuerzeListe(items);
    for (const it of gekuerzt.sichtbar) {
      const itm = it as Record<string, unknown>;
      const subject = trimText(ohneSchreibsteuer(String(itm.subject ?? "")) || "(kein Betreff)", 120);
      const to = mailEmpfaengerKurz(String(itm.to ?? ""));
      ul.appendChild(listenEintrag(subject, to ? `an ${to}` : "kein Empfänger"));
    }
    weitereHinweis(el, gekuerzt.rest);
  }
  return el;
}
